/* istanbul ignore file */

import React, { memo, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTrackEventEffect } from '@order/Telemetry';

import { useDeliveryOrderInFlight } from '../../hooks';
import { LocationWarningModal } from '../LocationWarningModal';
import { CompleteAddressFormModal, LocationSearchContent } from './components';
import { getAddressForm } from './helpers';
import { useLocationSearchMachine, useLocationSearchTelemetry, usePredictions } from './hooks';

import type { LocationSearchMachineContext } from './machine';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearch = memo((props: LocationSearchProps) => {
  const {
    isInsideModal,
    interactedLocationRestaurantSlug,
    interactedLocationDeliveryAddressId,
    navigateToLocation,
    navigateToDeliveryLocation,
    withNavHeader,
    onNavClose,
    shouldRequestLocationPermission,
  } = props;

  useDeliveryOrderInFlight();

  const { service, state, helpers, flags } = useLocationSearchMachine({
    navigateToLocation,
    navigateToDeliveryLocation,
    shouldRequestLocationPermission,
  });
  const { trackLocationResultsExpandCollapse } = useLocationSearchTelemetry();

  const { isRadarPickupAutocompleteEnabled } = usePredictions({
    locationSearchService: service,
  });

  const { context } = state;
  const {
    searchString,
    deliverySearchString,
    locations,
    locationSearchType,
    deliveryLocation,
    deliveryAddressPredictions,
    activeLocation,
    currentPosition,
    focusedPinId,
    focusedLocationId,
    recentLocations,
    nearbyLocations,
  } = context;

  const {
    setLocationSearchType,
    searchBasedOnCurrentPosition,
    searchLocationsByArea,
    searchDeliveryAddresses,
    setLocationSearchArea,
    setDeliveryPredictionPlaceId,
    setDeliveryPredictionDetails,
    setDeliveryCustomerAddress,
    setFocusedLocation,
    setFocusedLocationPin,
    confirmLocationWarning,
    cancelLocationWarning,
    navigateToSelectedLocation,
    navigateToSelectedDeliveryLocation,
    navigateToRecentOrNearbyLocation,
    cancelDeliveryAddressForm,
    submitDeliveryAddressForm,
    showRecentAndNearbyLocations,
  } = helpers;

  const {
    isLoadingLocations,
    isLoadingRecentAndNearbyLocations,
    isLoadingPredictions,
    isAddingAddress,
    isUsingSearchField,
    shouldShowLocationWarning,
    shouldRenderRecentAndNearbyLocations,
    shouldRenderDeliveryAddressForm,
  } = flags;

  const shouldHideOutpostResults = useFeatureFlag('em-888-hide-outpost-from-map-pickup-section');

  const filteredLocations = useMemo<LocationSearchMachineContext['locations']>(() => {
    if (!locations) return;

    if (locationSearchType === 'pickup') {
      return { ...locations, outpost: [] };
    }

    return locations;
  }, [locationSearchType, locations]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventEffect({
    name: 'location.search.view',
    // @ts-expect-error TS(2741): isPredictionsFeatureEnabled may be required by telemetry type
    payload: { isRadarPickupAutocompleteEnabled },
  });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <LocationSearchContent
        locationSearchService={service}
        locationSearchType={locationSearchType}
        searchString={searchString}
        deliverySearchString={deliverySearchString}
        currentPosition={currentPosition}
        locations={shouldHideOutpostResults ? filteredLocations : locations}
        recentLocations={recentLocations}
        nearbyLocations={nearbyLocations}
        deliveryLocation={deliveryLocation}
        shouldRenderRecentAndNearbyLocations={shouldRenderRecentAndNearbyLocations}
        predictionDeliveryAddresses={deliveryAddressPredictions}
        focusedPinId={focusedPinId}
        focusedLocationId={focusedLocationId}
        isLoadingPredictions={isLoadingPredictions}
        isLoadingLocations={isLoadingLocations}
        isLoadingRecentAndNearbyLocations={isLoadingRecentAndNearbyLocations}
        isUsingSearchField={isUsingSearchField}
        interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
        interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
        isInsideModal={isInsideModal}
        onLocationSearchTypeChange={setLocationSearchType}
        onLocationsSearchAreaChange={setLocationSearchArea}
        onDeliverySearchStringChange={searchDeliveryAddresses}
        onGetCurrentPosition={searchBasedOnCurrentPosition}
        onPredictionPress={setDeliveryPredictionPlaceId}
        onRadarPredictionPress={setDeliveryPredictionDetails}
        onCustomerAddressPress={setDeliveryCustomerAddress}
        onSearchAreaPress={searchLocationsByArea}
        onMapPinPress={setFocusedLocation}
        onLocationPinPress={navigateToSelectedLocation}
        onDeliveryPinPress={navigateToSelectedDeliveryLocation}
        onLocationCardFocus={setFocusedLocationPin}
        onLocationCardPress={navigateToSelectedLocation}
        onDeliveryLocationCardPress={navigateToSelectedDeliveryLocation}
        onLocationResultsExpandCollapse={trackLocationResultsExpandCollapse}
        onRecentOrNearbyLocationCardPress={navigateToRecentOrNearbyLocation}
        showRecentAndNearbyLocations={showRecentAndNearbyLocations}
        withNavHeader={withNavHeader}
        onNavClose={onNavClose}
      />

      {deliveryLocation ? (
        <CompleteAddressFormModal
          address={getAddressForm(deliveryLocation)}
          isVisible={shouldRenderDeliveryAddressForm}
          isAddingAddress={isAddingAddress}
          onRequestClose={cancelDeliveryAddressForm}
          onSubmit={submitDeliveryAddressForm}
        />
      ) : null}

      {shouldShowLocationWarning && activeLocation ? (
        <View style={StyleSheet.absoluteFill}>
          <LocationWarningModal
            locations={[activeLocation]}
            onClose={cancelLocationWarning}
            onConfirm={confirmLocationWarning}
            restaurantSlug={activeLocation.restaurantSlug}
          />
        </View>
      ) : null}
    </>
  );
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchProps = Readonly<{
  isInsideModal?: boolean;

  /**
   * While interacting with a location card (reordering, navigation, etc.),
   * we occasionally need to display the loading, disabled states.
   * To display the corresponding additional state, we match the target location
   * card restaurant slug to the interacted location restaurant slug and/or delivery address ID.
   * */
  interactedLocationRestaurantSlug: string | undefined;
  interactedLocationDeliveryAddressId: string | undefined;

  /*
    Optional actions that initiates the navigation process for the selected location.
    When those properties are not provided, the standard navigation is used.

    Helpful when a custom navigation method is utilized or when other actions
    need to be performed before navigation.
   */

  navigateToLocation?: (context: LocationSearchMachineContext) => void;
  navigateToDeliveryLocation?: (context: LocationSearchMachineContext) => void;

  withNavHeader?: boolean;
  onNavClose?: () => void;

  /**
   * When `false`, the search machine skips the setup phase that would
   * otherwise request the OS location permission. Useful when an upstream
   * consumer (e.g. the location permission primer) owns that flow.
   */
  shouldRequestLocationPermission?: boolean;
}>;
